/**
 * Log level types supported by the logger
 */
type LogLevel = "log" | "info" | "warn" | "error" | "debug";

/**
 * Logger interface providing development-only logging capabilities
 */
interface Logger {
  /** Standard log output */
  log: (...args: unknown[]) => void;
  /** Informational messages */
  info: (...args: unknown[]) => void;
  /** Warning messages */
  warn: (...args: unknown[]) => void;
  /** Error messages */
  error: (...args: unknown[]) => void;
  /** Debug messages */
  debug: (...args: unknown[]) => void;
}

/**
 * Creates a logger instance that only outputs in development mode
 * @returns Logger instance with console methods that are no-ops in production
 * @example
 * import { logger } from './utils/logger'
 * logger.log('Debug info') // Only logs in development
 * logger.error('Error occurred', error) // Only logs in development
 */
const createLogger = (): Logger => {
  const isDevelopment = import.meta.env.MODE === "development";

  const logMethod =
    (level: LogLevel) =>
    (...args: unknown[]): void => {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console[level](...args);
      }
    };

  return {
    log: logMethod("log"),
    info: logMethod("info"),
    warn: logMethod("warn"),
    error: logMethod("error"),
    debug: logMethod("debug"),
  };
};

/**
 * Global logger instance for the application
 * Automatically handles development/production mode switching
 * @example
 * import { logger } from '@/utils/logger'
 *
 * // Use throughout your application
 * logger.log('Component mounted')
 * logger.warn('Deprecated API usage')
 * logger.error('Failed to fetch data', error)
 */
export const logger = createLogger();
